import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase';
import PageHeader from '../components/PageHeader';
import { useConfirm } from '../components/ConfirmDialog';

// إدارة الأنشطة + سجل الحضور. الأنشطة "مفتوحة" تظهر للمتطوعين ف الموبايل
// عشان يسجّلوا فيها. الحضور بيتسجّل هنا: مين حضر، كام ساعة، هل اتواصلنا معاه.

type Event = {
  id: number;
  name: string;
  name_en: string | null;
  description: string | null;
  description_en: string | null;
  location: string | null;
  location_en: string | null;
  address: string | null;
  address_en: string | null;
  is_open: boolean;
  event_date: string | null;
  time_from: string | null;
  time_to: string | null;
  committee_id: number | null;
  created_at: string;
};

type Attendance = {
  id: number;
  volunteer_id: number;
  event_id: number;
  event_date: string | null;
  shift_text: string | null;
  present: boolean;
  hours: number;
  attend_note: string | null;
  contacted: boolean;
  call_result: string | null;
};

type Volunteer = { id: number; full_name: string; phone: string | null };
type Committee = { id: number; name: string };

type Draft = Omit<Event, 'id' | 'created_at'> & { id?: number };
const emptyDraft: Draft = {
  name: '',
  name_en: '',
  description: '',
  description_en: '',
  location: '',
  location_en: '',
  address: '',
  address_en: '',
  is_open: false,
  event_date: null,
  time_from: '',
  time_to: '',
  committee_id: null,
};

export default function VolunteeringActivitiesPage() {
  const [events, setEvents] = useState<Event[]>([]);
  const [committees, setCommittees] = useState<Committee[]>([]);
  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [editing, setEditing] = useState<Draft | null>(null);
  const [creating, setCreating] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [attendance, setAttendance] = useState<Attendance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const confirm = useConfirm();

  useEffect(() => { void load(); }, []);
  useEffect(() => { if (selectedEvent) void loadAttendance(selectedEvent.id); }, [selectedEvent]);

  const load = async () => {
    setLoading(true);
    const [e, c, v] = await Promise.all([
      supabase.from('vol_events').select('*').order('event_date', { ascending: false }),
      supabase.from('vol_committees').select('id, name'),
      supabase.from('volunteers').select('id, full_name, phone').eq('application_status', 'ACCEPTED'),
    ]);
    if (e.error) setError(e.error.message);
    else setEvents((e.data ?? []) as Event[]);
    setCommittees((c.data ?? []) as Committee[]);
    setVolunteers((v.data ?? []) as Volunteer[]);
    setLoading(false);
  };

  const loadAttendance = async (eventId: number) => {
    const { data, error: err } = await supabase.from('vol_attendance').select('*').eq('event_id', eventId).order('id');
    if (err) setError(err.message);
    else setAttendance((data ?? []) as Attendance[]);
  };

  const saveEvent = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    const { error: err } = creating
      ? await supabase.from('vol_events').insert(editing)
      : await supabase.from('vol_events').update(editing).eq('id', editing.id!);
    if (err) return setError(err.message);
    setEditing(null);
    setCreating(false);
    await load();
  };

  const deleteEvent = async (id: number) => {
    if (!(await confirm({
      title: 'حذف النشاط؟',
      body: 'كل الحضور المرتبط بيه هيتحذف. مش هيقدر يترجع.',
      confirmText: 'احذف',
      tone: 'danger',
    }))) return;
    const { error: err } = await supabase.from('vol_events').delete().eq('id', id);
    if (err) setError(err.message);
    else await load();
  };

  const addAttendance = async () => {
    if (!selectedEvent) return;
    const { error: err } = await supabase.from('vol_attendance').insert({
      event_id: selectedEvent.id,
      volunteer_id: volunteers[0]?.id ?? 1,
      present: false,
      hours: 0,
    });
    if (err) setError(err.message);
    else await loadAttendance(selectedEvent.id);
  };

  // U-ADM-11 · pre-launch audit hardening. Row edits used to fire a
  // DB UPDATE on every keystroke in shift_text / hours / attend_note.
  // A single-sentence note = ~30 UPDATEs. Debounce writes to 400ms
  // after the last change so the DB (and Realtime fanout) only sees
  // one write per typed sentence. Optimistic UI stays instant.
  const attendanceTimers = useMemo(() => new Map<number, ReturnType<typeof setTimeout>>(), []);

  const updateAttendance = (row: Attendance) => {
    // Optimistic UI: reflect the new value in state immediately.
    setAttendance((prev) => prev.map((a) => (a.id === row.id ? row : a)));

    const existing = attendanceTimers.get(row.id);
    if (existing) clearTimeout(existing);
    attendanceTimers.set(
      row.id,
      setTimeout(() => {
        attendanceTimers.delete(row.id);
        void (async () => {
          const { error: err } = await supabase
            .from('vol_attendance')
            .update(row)
            .eq('id', row.id);
          if (err) setError(err.message);
        })();
      }, 400),
    );
  };

  useEffect(() => {
    return () => {
      attendanceTimers.forEach((t) => clearTimeout(t));
      attendanceTimers.clear();
    };
  }, [attendanceTimers]);

  const volunteerName = (id: number) => volunteers.find((v) => v.id === id)?.full_name ?? `#${id}`;

  return (
    <>
      <PageHeader
        eyebrow="التطوع"
        title="الأنشطة والحضور"
        subtitle="أنشئ نشاط جديد، افتحه للتسجيل، وسجّل حضور المتطوعين للحصول على تقارير الساعات"
        actions={<button className="btn" onClick={() => { setCreating(true); setEditing({ ...emptyDraft }); }}>+ نشاط جديد</button>}
      />
      {error && <div className="error-inline">{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 3fr', gap: 16 }}>
        <div>
          <h3 style={{ marginTop: 0 }}>الأنشطة</h3>
          {loading ? (
            <div className="empty">جاري التحميل...</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {events.map((ev) => (
                <div key={ev.id} className="card" style={{ cursor: 'pointer', border: selectedEvent?.id === ev.id ? '2px solid var(--teal)' : undefined }} onClick={() => setSelectedEvent(ev)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div>
                      <strong>{ev.name}</strong>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {ev.event_date ?? 'بدون تاريخ'} · {ev.location ?? '—'}
                      </div>
                    </div>
                    <span className={`badge ${ev.is_open ? 'badge-success' : 'badge-muted'}`}>
                      {ev.is_open ? 'مفتوح' : 'مغلق'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <button className="btn btn-small btn-secondary" onClick={(e) => { e.stopPropagation(); setCreating(false); setEditing({ ...ev }); }}>تعديل</button>
                    <button className="btn btn-small btn-danger" onClick={(e) => { e.stopPropagation(); void deleteEvent(ev.id); }}>حذف</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <h3 style={{ marginTop: 0 }}>{selectedEvent ? `الحضور: ${selectedEvent.name}` : 'اختر نشاط'}</h3>
          {selectedEvent ? (
            <>
              <button className="btn btn-small" onClick={() => void addAttendance()}>+ سطر حضور</button>
              <table className="table" style={{ marginTop: 8 }}>
                <thead>
                  <tr>
                    <th>المتطوع</th>
                    <th>الوردية</th>
                    <th>حضر؟</th>
                    <th>الساعات</th>
                    <th>ملاحظات</th>
                    <th>اتواصلنا؟</th>
                  </tr>
                </thead>
                <tbody>
                  {attendance.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <select value={a.volunteer_id} onChange={(e) => void updateAttendance({ ...a, volunteer_id: Number(e.target.value) })}>
                          {volunteers.map((v) => (<option key={v.id} value={v.id}>{v.full_name}</option>))}
                        </select>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{volunteerName(a.volunteer_id)}</div>
                      </td>
                      <td><input value={a.shift_text ?? ''} onChange={(e) => void updateAttendance({ ...a, shift_text: e.target.value })} placeholder="10 صباحًا - 4 عصرًا" /></td>
                      <td><input type="checkbox" checked={a.present} onChange={(e) => void updateAttendance({ ...a, present: e.target.checked })} /></td>
                      <td><input type="number" step="0.5" value={a.hours} onChange={(e) => void updateAttendance({ ...a, hours: Number(e.target.value) })} style={{ width: 70 }} /></td>
                      <td><input value={a.attend_note ?? ''} onChange={(e) => void updateAttendance({ ...a, attend_note: e.target.value })} /></td>
                      <td><input type="checkbox" checked={a.contacted} onChange={(e) => void updateAttendance({ ...a, contacted: e.target.checked })} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <div className="card empty">اختر نشاط من الشمال</div>
          )}
        </div>
      </div>

      {editing && (
        <div className="modal-backdrop" onClick={() => setEditing(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{creating ? 'نشاط جديد' : `تعديل: ${editing.name}`}</h2>
              <button className="btn btn-small btn-secondary" onClick={() => setEditing(null)}>إغلاق</button>
            </div>
            <form onSubmit={saveEvent}>
              <div className="row">
                <div className="field"><label>اسم النشاط (عربي)</label><input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
                <div className="field"><label>Activity name (English)</label><input value={editing.name_en ?? ''} onChange={(e) => setEditing({ ...editing, name_en: e.target.value })} /></div>
              </div>
              <div className="row">
                <div className="field"><label>الوصف (عربي)</label><textarea rows={2} value={editing.description ?? ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
                <div className="field"><label>Description (English)</label><textarea rows={2} value={editing.description_en ?? ''} onChange={(e) => setEditing({ ...editing, description_en: e.target.value })} /></div>
              </div>
              <div className="row">
                <div className="field"><label>اللوكيشن (عربي)</label><input value={editing.location ?? ''} onChange={(e) => setEditing({ ...editing, location: e.target.value })} /></div>
                <div className="field"><label>Location (English)</label><input value={editing.location_en ?? ''} onChange={(e) => setEditing({ ...editing, location_en: e.target.value })} /></div>
              </div>
              <div className="row">
                <div className="field"><label>العنوان التفصيلي (عربي)</label><input value={editing.address ?? ''} onChange={(e) => setEditing({ ...editing, address: e.target.value })} /></div>
                <div className="field"><label>Detailed address (English)</label><input value={editing.address_en ?? ''} onChange={(e) => setEditing({ ...editing, address_en: e.target.value })} /></div>
              </div>
              <div className="row">
                <div className="field"><label>التاريخ</label><input type="date" value={editing.event_date ?? ''} onChange={(e) => setEditing({ ...editing, event_date: e.target.value || null })} /></div>
                <div className="field"><label>من</label><input value={editing.time_from ?? ''} onChange={(e) => setEditing({ ...editing, time_from: e.target.value })} placeholder="10 صباحًا" /></div>
                <div className="field"><label>إلى</label><input value={editing.time_to ?? ''} onChange={(e) => setEditing({ ...editing, time_to: e.target.value })} placeholder="4 عصرًا" /></div>
              </div>
              <div className="row">
                <div className="field">
                  <label>اللجنة</label>
                  <select value={editing.committee_id ?? ''} onChange={(e) => setEditing({ ...editing, committee_id: e.target.value ? Number(e.target.value) : null })}>
                    <option value="">—</option>
                    {committees.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                  </select>
                </div>
                <div className="field">
                  <label>مفتوح للتسجيل؟</label>
                  <select value={editing.is_open ? 'y' : 'n'} onChange={(e) => setEditing({ ...editing, is_open: e.target.value === 'y' })}>
                    <option value="y">نعم</option>
                    <option value="n">لا</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)}>إلغاء</button>
                <button type="submit" className="btn">{creating ? 'نشر' : 'حفظ'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
